---
title: "How to save Excel as PDF without cutting off columns"
description: "Excel PDF export keeps splitting your table across pages? Here is what scale, landscape mode and print area actually do."
locale: "en"
date: "2026-05-18"
tags: ["excel", "pdf", "spreadsheets"]
relatedTools: ["excelVPdf"]
translations:
  ru: "kak-sohranit-excel-v-pdf-chtoby-ne-obrezalos"
faq:
  - q: "Why does Excel cut off columns when exporting to PDF?"
    a: "Because the PDF is built from a print layout. If the sheet is wider than the selected paper size, Excel either moves the extra columns to another page or respects an old print area that no longer matches the sheet."
  - q: "Can I force all columns onto one PDF page?"
    a: "Often, yes. Excel has a `Fit to 1 page wide` setting, and the converter below fits all columns to the page width. But if the sheet has 28 wide columns, fitting them onto A4 just makes the text tiny."
  - q: "Is the file processed locally or uploaded to a server?"
    a: "Locally. The spreadsheet is read and built into a PDF right in the browser, and the file is not uploaded. Styling is not kept: only cell values are carried over."
  - q: "What should I check before sending the PDF?"
    a: "Open the result and inspect the first and last pages. Make sure the rightmost columns are visible, there is no empty page at the end, and the headers are still readable."
---

import { ExcelVPdfForm } from '@/pages/excel-v-pdf/ExcelVPdfForm'

`april_report.xlsx`: 17 columns, two sheets, and a very important
`Total` column on the far right. In Excel it looks fine. You export to
PDF, send it out, and the first 13 columns land on page one while the
last 4 move to page two. Technically the file is valid. Practically it
is a bad report.

The converter below builds the PDF right in your browser: the file goes
nowhere, and all columns are fitted to the width of landscape A4. Colors
and merged cells are not kept. No sign-up, no watermarks.

<div className="my-8 rounded-xl border bg-card p-5 shadow-sm">
  <ExcelVPdfForm />
</div>

## Why Excel splits the table

Excel does not think in pages while you are editing a spreadsheet. It is
an endless grid where 200 columns to the right still feel normal. PDF is
the opposite: A4, A3, portrait, landscape, margins, scale. When you
export, Excel has to squeeze that grid into a print layout.

The usual mistake is exporting without looking at the print preview. A
portrait A4 page fits maybe 7-10 ordinary columns. Landscape gives you
more room, but not infinite room. Long headers like `Manager comment on
overdue payment` burn through page width faster than they look on
screen.

Microsoft calls the useful setting `Scale to Fit`: make the sheet fit
one page wide. It is a good setting, with one catch. Excel does not
redesign your table. It just shrinks it. For a 12-column report this can
work well. For a 38-column CRM export it turns into self-deception.

## Where the fix usually is

Start with preview, not with another converter. If the table is wider
than it is tall, it almost always wants landscape. Obvious, yes, but a
surprising number of broken Excel PDFs begin with portrait A4 silently
splitting a report sideways.

Then check the print area. Old spreadsheets often carry a print area
from a previous version of the file. Someone selected `A1:M42`, added
four new columns a month later, and the PDF export kept obeying the old
rectangle. From the outside it looks like the converter lost data. The
file told it to.

There is also the empty-tail problem. Excel treats formatting as part of
the used range. If someone painted an entire far-right column or a few
thousand empty rows, the PDF export may suddenly produce blank pages.
That is not a clever compression problem. Delete the unused rows and
columns.

## When an online converter is more convenient

Smallpdf, iLovePDF and similar tools already cover the broad `Excel to
PDF` query: upload the file, download the result. That is useful when
Excel is not installed, the file arrived on someone else's laptop, or
you just need a PDF quickly. What those pages usually skip is the boring
reason why a table got torn apart in the first place.

The logic here is more down to earth. The spreadsheet is built locally:
SheetJS reads the workbook, pdf-lib builds the PDF, and the file does not
leave the browser. The price is visible right away: styling is not carried
over and pages come out as images. If the table is styled and the exact look
matters, save the PDF from Excel itself using the tips above.

No button can make a very wide spreadsheet pleasant on A4. If you have
31 columns, 8 pt text and grouped headers, the real fix is a layout
decision: remove columns, split the report, use A3, or keep Excel as the
source format. PDF is a final snapshot, not a stretchy spreadsheet.

After conversion, open the result and look at the right edge. If `Total`
is visible, there is no blank page at the end, and the headers are
readable without zooming to 180%, the file is ready to send. If not, the
problem is no longer PDF conversion. The table needs layout work.
